"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useEnergyTrends = void 0;
const react_1 = require("react");
const costSeries_1 = require("../../utils/energy/costSeries");
const energyTrends_1 = require("../../utils/energy/energyTrends");
/**
 * State and numbers behind Energy Usage Trends: one Day / Week / Month period, its circuit
 * rows and bars, and the comparison with the period before (Day: the same day last week).
 * Mobile keeps one per card; the hub keeps ONE per screen so its list card and chart card
 * always show the same period, units and circuit. Labels stay in the apps (no i18n here).
 */
const useEnergyTrends = (source, otherName) => {
    const { getRangeBreakdown, getSeries, todayHoursWithData, earliestDate, todayDate } = source;
    const [mode, setMode] = (0, react_1.useState)('day');
    const [anchor, setAnchor] = (0, react_1.useState)(todayDate);
    const [unit, setUnit] = (0, react_1.useState)('cost');
    // On by default: the % vs last week the old Today's Energy Cost card always showed.
    const [compare, setCompare] = (0, react_1.useState)(true);
    // Kept whole, not just the key: its name still shows in a period where it has no data.
    const [selected, setSelected] = (0, react_1.useState)(null);
    const { start, end } = (0, react_1.useMemo)(() => (0, energyTrends_1.periodRange)(mode, anchor), [mode, anchor]);
    const breakdown = (0, react_1.useMemo)(() => getRangeBreakdown(start, end), [getRangeBreakdown, start, end]);
    // Navigation sets the anchor to the new period's START. Prev requires the whole
    // previous period inside the fetched window — a partially-covered period would
    // silently show partial totals as if they were the real thing.
    const prevStart = (0, energyTrends_1.shiftPeriod)(mode, start, -1);
    const nextStart = (0, energyTrends_1.shiftPeriod)(mode, start, 1);
    const canGoPrev = prevStart >= earliestDate;
    const canGoNext = nextStart <= todayDate;
    // Only inside the fetched window, or every bar would read 0 and look like a real "used nothing".
    const compareStart = (0, energyTrends_1.comparePeriodStart)(mode, start);
    const comparePeriod = (0, react_1.useMemo)(() => (compareStart >= earliestDate ? (0, energyTrends_1.periodRange)(mode, compareStart) : null), [mode, compareStart, earliestDate]);
    const inProgress = end >= todayDate;
    const tooEarly = inProgress && todayHoursWithData < energyTrends_1.MIN_COMPARE_HOURS;
    const compareStatus = !compare
        ? 'off'
        : !comparePeriod
            ? 'unavailable'
            : tooEarly
                ? 'tooEarly'
                : inProgress
                    ? 'soFar'
                    : 'full';
    const bucket = mode === 'day' ? 'hour' : 'day';
    const series = (0, react_1.useMemo)(() => getSeries(start, end, bucket, selected?.key), [getSeries, start, end, bucket, selected?.key]);
    // Chart pairs: the whole compared period, bar-for-bar (hour 3 vs hour 3, day 5 vs day 5);
    // a shorter previous month leaves its last slots empty (key '').
    const previousSeries = (0, react_1.useMemo)(() => {
        if (!comparePeriod || !compare)
            return undefined;
        const points = getSeries(comparePeriod.start, comparePeriod.end, bucket, selected?.key);
        return series.map((_, index) => points[index] ?? { key: '', kwh: 0, cost: 0 });
    }, [comparePeriod, compare, getSeries, bucket, selected?.key, series]);
    // Badges compare the SAME stretch: a period still running is cut to match (sameStretchEnd).
    const compareBreakdown = (0, react_1.useMemo)(() => {
        if (!comparePeriod || !compare || tooEarly)
            return null;
        if (!inProgress)
            return getRangeBreakdown(comparePeriod.start, comparePeriod.end);
        const stretch = (0, energyTrends_1.sameStretchEnd)(start, comparePeriod, todayDate, todayHoursWithData);
        return getRangeBreakdown(comparePeriod.start, stretch.end, stretch.endHours);
    }, [
        comparePeriod,
        compare,
        tooEarly,
        inProgress,
        start,
        todayDate,
        todayHoursWithData,
        getRangeBreakdown,
    ]);
    const rows = (0, react_1.useMemo)(() => {
        const change = (kwh, earlier) => compareBreakdown ? (0, energyTrends_1.changePercent)(kwh, earlier ?? 0) : null;
        return [
            ...breakdown.branches.map((b) => ({
                key: b.key,
                name: b.name,
                cost: b.cost,
                kwh: b.kwh,
                percent: b.percent,
                change: change(b.kwh, compareBreakdown?.branches.find((e) => e.key === b.key)?.kwh),
            })),
            {
                key: costSeries_1.OTHER_ROW_KEY,
                name: otherName,
                cost: breakdown.other.cost,
                kwh: breakdown.other.kwh,
                percent: breakdown.other.percent,
                isOther: true,
                change: change(breakdown.other.kwh, compareBreakdown?.other.kwh),
            },
        ];
    }, [breakdown, compareBreakdown, otherName]);
    // One object per real change: the hub shares it through a context, so a fresh object on
    // every render (e.g. each household sensor update) re-rendered both trends cards.
    return (0, react_1.useMemo)(() => {
        const totalChange = compareBreakdown
            ? (0, energyTrends_1.changePercent)(breakdown.totalKwh, compareBreakdown.totalKwh)
            : null;
        // What the chart shows: the tapped row, else the whole panel.
        const shownRow = selected ? rows.find((r) => r.key === selected.key) : undefined;
        const headline = selected
            ? { cost: shownRow?.cost ?? 0, kwh: shownRow?.kwh ?? 0, change: shownRow?.change ?? null }
            : { cost: breakdown.totalCost, kwh: breakdown.totalKwh, change: totalChange };
        const selectMode = (next) => {
            setMode(next);
            setAnchor(todayDate);
        };
        return {
            mode,
            unit,
            compare,
            selected,
            start,
            end,
            comparePeriod,
            compareStatus,
            canGoPrev,
            canGoNext,
            onToday: mode === 'day' && anchor === todayDate,
            breakdown,
            rows,
            totalChange,
            headline,
            series,
            previousSeries,
            hasData: breakdown.branches.length > 0 || breakdown.other.kwh > 0,
            selectMode,
            goPrev: () => canGoPrev && setAnchor(prevStart),
            goNext: () => canGoNext && setAnchor(nextStart),
            skipToToday: () => selectMode('day'),
            setUnit,
            toggleCompare: () => setCompare((on) => !on),
            selectRow: (row) => setSelected(row && { key: row.key, name: row.name, isOther: row.isOther }),
        };
    }, [
        mode,
        unit,
        compare,
        selected,
        anchor,
        todayDate,
        start,
        end,
        comparePeriod,
        compareStatus,
        canGoPrev,
        canGoNext,
        prevStart,
        nextStart,
        breakdown,
        compareBreakdown,
        rows,
        series,
        previousSeries,
    ]);
};
exports.useEnergyTrends = useEnergyTrends;
